import { useEffect, useRef, useState } from "react";
import { GTVMessage } from "../util/types";
import { WEBSOCKET_URL } from "../util/consts";

type MessageCallback = (msg: GTVMessage) => void | Promise<void>;

export function useWebSocket(onMessage: MessageCallback): {
  socketConnected: boolean;
  sendSocketMessage: (message: GTVMessage) => void | Promise<void>;
} {
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<WebSocket | null>(null);
  const [pingInterval, setPingInterval] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (socketRef.current || isConnected) {
      return;
    }
    socketRef.current = new WebSocket(WEBSOCKET_URL);
    // Set up WebSocket event handlers
    socketRef.current.onopen = () => {
      setIsConnected(true);
      // set up a ping every 5 seconds to keep the connection alive
      const ping = setInterval(() => {
        if (
          socketRef.current &&
          socketRef.current.readyState === WebSocket.OPEN
        ) {
          const pingMessage: GTVMessage = {
            Type: "Ping",
          };
          socketRef.current.send(JSON.stringify(pingMessage));
        }
      }, 5000); // 5 seconds
      setPingInterval(ping);
    };
    socketRef.current.onmessage = async (event) => {
      try {
        const message: GTVMessage = JSON.parse(event.data);
        await onMessage(message);
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };
    socketRef.current.onerror = (error) => {
      console.error("WebSocket error:", error);
      setIsConnected(false);
      if (pingInterval) {
        clearInterval(pingInterval);
        setPingInterval(null);
      }
    };
    socketRef.current.onclose = (event) => {
      setIsConnected(false);
      console.log(
        "WebSocket connection closed:",
        event.reason,
        ", code:",
        event.code
      );
      if (pingInterval) {
        clearInterval(pingInterval);
        setPingInterval(null);
      }
      // Optionally, you can implement reconnection logic here
      if (event.code !== 1000) {
        console.log("Attempting to reconnect...");
        setTimeout(() => {
          // Reconnect logic
          socketRef.current = new WebSocket(WEBSOCKET_URL);
        }, 5000); // Reconnect after 5 seconds
      }
    };
  }, [isConnected, pingInterval, onMessage]);
  const sendMessage = (message: GTVMessage) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      try {
        const messageString = JSON.stringify(message);
        socketRef.current.send(messageString);
        console.log("WebSocket Message sent:", message);
      } catch (error) {
        console.error("Error sending WebSocket message:", error);
      }
    } else {
      console.warn("WebSocket is not open. Cannot send message:", message);
    }
  };
  return { socketConnected: isConnected, sendSocketMessage: sendMessage };
}
